/**
 * src/lib/cart.js
 * localStorage-backed cart. Each line is keyed by stockId (the actual
 * barcode/size row), not just a size label — two different products could
 * both have a "4-5 Years" size, but they're different Stock rows.
 *
 * This client-side cap on quantity is just UX help (don't let someone add
 * 10 of something with 2 left) — the real, authoritative check happens
 * server-side in place_online_order, which locks the row and re-verifies
 * availability at checkout regardless of what's shown here.
 */

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "ritu_cart_v1";

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function persist(lines) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  } catch {
    /* ignore — storage full/unavailable, cart just won't persist this session */
  }
}

// Simple event bus so multiple components using useCart() stay in sync
// without needing a context provider.
const listeners = new Set();
function notify() {
  listeners.forEach((fn) => fn());
}

export function useCart() {
  const [lines, setLines] = useState(load);

  useEffect(() => {
    const onChange = () => setLines(load());
    listeners.add(onChange);
    return () => listeners.delete(onChange);
  }, []);

  const addItem = useCallback((product, size, qty = 1) => {
    const current = load();
    const idx = current.findIndex((l) => l.stockId === size.stockId);
    let next;
    if (idx >= 0) {
      const newQty = Math.min(current[idx].qty + qty, size.quantity);
      next = [...current];
      next[idx] = { ...next[idx], qty: newQty };
    } else {
      next = [
        ...current,
        {
          stockId: size.stockId,
          productId: product.productId,
          slug: product.slug,
          name: product.name,
          image: product.images[0] || null,
          sizeLabel: size.sizeLabel,
          price: size.effectivePrice,
          maxQty: size.quantity,
          qty: Math.min(qty, size.quantity),
        },
      ];
    }
    persist(next);
    notify();
  }, []);

  const setQty = useCallback((stockId, qty) => {
    const current = load();
    const next = current
      .map((l) => (l.stockId === stockId ? { ...l, qty: Math.max(1, Math.min(qty, l.maxQty)) } : l))
      .filter((l) => l.qty > 0);
    persist(next);
    notify();
  }, []);

  const remove = useCallback((stockId) => {
    const next = load().filter((l) => l.stockId !== stockId);
    persist(next);
    notify();
  }, []);

  const clear = useCallback(() => {
    persist([]);
    notify();
  }, []);

  const subtotal = lines.reduce((sum, l) => sum + l.price * l.qty, 0);
  const itemCount = lines.reduce((sum, l) => sum + l.qty, 0);

  return { lines, addItem, setQty, remove, clear, subtotal, itemCount };
}